import React, { useRef } from 'react';
import { Card } from 'react-bootstrap';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react-thin';
import type { ColumnConfig } from '@bryntum/grid-thin';
import type { Model } from '@bryntum/core-thin';

import '../../../../../styles/bryntum/bryntum-schedulerpro.css';
import './styles.css';
import { renderShiftEvent } from './eventRenderer';
import { renderResourceCell } from './resourceRenderer';
import ColleaguesToolbar from './ColleaguesToolbar';
import { useColleaguesCalendarData } from './useColleaguesCalendarData';
import { getFillTicks, getViewPreset } from './utils';
import { useCurrentLocale } from '../../../../../context/I18n';
import { SchedulerLoading } from './loading/SchedulerLoading';
import { useSchedulerSetup } from './useSchedulerSetup';
import { useSchedulerNavigation } from './useSchedulerNavigation';

const RESOURCE_COLUMN: ColumnConfig = {
  field: 'name',
  text: '',
  autoWidth: true,
  minWidth: '10rem',
  renderer: ({ record }: { record: Model }) => renderResourceCell(record),
  filterable: true,
};

export const ColleaguesCalendar: React.FC = () => {
  const locale = useCurrentLocale();
  const schedulerRef = useRef<BryntumSchedulerPro>(null);

  const {
    view,
    currentDate,
    startDate,
    endDate,
    schedulers,
    effectiveScheduler,
    allTargets,
    effectiveTargetIds,
    resources,
    events,
    initialLoading,
    dataLoading,
    handleSchedulerChange,
    handleTargetsChange,
    handleViewChange: onViewChange,
    handleDateChange,
  } = useColleaguesCalendarData();

  useSchedulerSetup(schedulerRef, {
    locale,
    resources,
    initialLoading,
    startDate,
    endDate,
  });

  const {
    handleViewChange,
    handleNavigatePrevious,
    handleNavigateNext,
    handleNavigateToday,
  } = useSchedulerNavigation(schedulerRef, {
    view,
    currentDate,
    onViewChange,
    onDateChange: handleDateChange,
  });

  return (
    <Card className="d-flex flex-column">
      <Card.Header className="px-3 py-0">
        <ColleaguesToolbar
          view={view}
          onViewChange={handleViewChange}
          schedulers={schedulers}
          selectedScheduler={effectiveScheduler}
          onSchedulerChange={handleSchedulerChange}
          targets={allTargets}
          selectedTargetIds={effectiveTargetIds}
          onTargetsChange={handleTargetsChange}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          onNavigateToday={handleNavigateToday}
          loading={initialLoading}
        />
      </Card.Header>
      <div className="flex-grow-1 min-h-0 position-relative scheduler-height">
        {initialLoading ? (
          <SchedulerLoading />
        ) : (
          <>
            <BryntumSchedulerPro
              ref={schedulerRef}
              resources={resources}
              events={events}
              viewPreset={getViewPreset(view)}
              columns={[RESOURCE_COLUMN] as ColumnConfig[]}
              barMargin={7}
              rowHeight={70}
              eventRenderer={({ eventRecord }) => renderShiftEvent(eventRecord)}
              eventDragFeature={false}
              eventResizeFeature={false}
              eventEditFeature={false}
              eventCopyPasteFeature={false}
              eventDragCreateFeature={false}
              eventDragSelectFeature={false}
              dependenciesFeature={false}
              taskEditFeature={false}
              eventMenuFeature={{
                items: { deleteEvent: false, splitEvent: false },
              }}
              scheduleMenuFeature={{
                items: { addEvent: false },
              }}
              cellMenuFeature={{ items: { removeRow: false } }}
              filterBarFeature={{ compactMode: true }}
              zoomOnMouseWheel={false}
              fillTicks={getFillTicks(view)}
              onScheduleDblClick={() => false}
            />
            {dataLoading && (
              <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
                <SchedulerLoading />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
